import {
  QUERY_BULK_UPDATE_CURB_ZONE,
  QUERY_CREATE_CURB_ZONE,
  QUERY_CURB_ZONES,
  QUERY_GET_TOTAL_CALCULATED_SPACES,
  QUERY_REMOVE_CURB_ZONE,
  QUERY_REMOVE_CURB_ZONES,
  QUERY_UPDATE_CURB_ZONE,
} from "services/web/api-curb-zone.const";
import { hideGlobalLoading, showGlobalLoading } from "@/hooks/use-global-loading";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTrackedMutation, useTrackedQuery } from "@/hooks/apollo-insights-hooks";

import { CityContext } from "@/contexts/city-context-provider";
import { CurbZone } from "@/types/curb-zone.type";
import { CurbZoneParams } from "@/modules/curb-zone/curb-zone.type";
import { Range } from "react-date-range";
import { formatRangeDate } from "@/helper/date-time";
import { getQueryParams } from "@/helper/utils";
import { skipToken } from "@apollo/client/react";
import { useDebounce } from "@/hooks/lib-ui";
import { useSearchFilterCurbZone } from "@/hooks/use-search-filter-curb-zone";

export const useQueryCurbZone = () => {
  const { currentCity } = useContext(CityContext);
  const [isResetData, setIsResetData] = useState<boolean>(false);
  const [bounding, setBounding] = useState<number[][]>();
  const nearBy = useDebounce(bounding, 500);
  const { filter, changeFilter } = useSearchFilterCurbZone();
  const params = getQueryParams<CurbZoneParams>();
  const [selectedDate, setSelectedDate] = useState<Pick<Range, "startDate" | "endDate">>(() => ({
    startDate: params.startDate ? new Date(params.startDate) : undefined,
    endDate: params.endDate ? new Date(params.endDate) : undefined,
  }));

  const totalSpacesFilterInput = useMemo(() => {
    if (!currentCity?.id) return null;

    const dateRange =
      selectedDate.startDate && selectedDate.endDate
        ? formatRangeDate(selectedDate.startDate, selectedDate.endDate)
        : {
            startDate: null,
            endDate: null,
          };

    return {
      cityId: currentCity.id,
      ...dateRange,
    };
  }, [currentCity?.id, selectedDate]);

  const refetchTotalSpacesQueries = useMemo(
    () =>
      totalSpacesFilterInput
        ? {
            refetchQueries: [
              {
                query: QUERY_GET_TOTAL_CALCULATED_SPACES,
                variables: {
                  filterInput: totalSpacesFilterInput,
                },
              },
            ],
            awaitRefetchQueries: true,
          }
        : null,
    [totalSpacesFilterInput],
  );

  const curbZonesOptions = useMemo(() => {
    if (currentCity?.id && nearBy) {
      return {
        variables: {
          filterInput: {
            cityId: currentCity.id,
            boundingbox: nearBy,
            policies: filter.selectedPolicies.length > 0 ? filter.selectedPolicies : undefined,
          },
        },
        actionName: "QueryCurbZones",
      };
    }
    return skipToken;
  }, [currentCity?.id, nearBy, filter.selectedPolicies]);

  const { data, loading } = useTrackedQuery(QUERY_CURB_ZONES, curbZonesOptions);

  const totalCalculatedSpacesOptions = useMemo(() => {
    if (totalSpacesFilterInput) {
      return {
        variables: {
          filterInput: totalSpacesFilterInput,
        },
        actionName: "GetTotalCalculatedSpaces",
      };
    }
    return skipToken;
  }, [totalSpacesFilterInput]);

  const { data: totalCalculatedSpacesResponse } = useTrackedQuery(
    QUERY_GET_TOTAL_CALCULATED_SPACES,
    totalCalculatedSpacesOptions,
  );
  useEffect(() => {
    if (loading) {
      showGlobalLoading();
    } else {
      hideGlobalLoading();
    }
  }, [loading]);

  const curbZones = useMemo(() => {
    return (data?.curbZones || []) as CurbZone[];
  }, [data]);

  const resetState = () => {
    setIsResetData(true);
  };

  const [createCurbZone] = useTrackedMutation(QUERY_CREATE_CURB_ZONE, {
    ...refetchTotalSpacesQueries,
    onError: resetState,
  });

  const [updateCurbZone] = useTrackedMutation(QUERY_UPDATE_CURB_ZONE, {
    ...refetchTotalSpacesQueries,
    onError: resetState,
  });

  const [removeCurbZone] = useTrackedMutation(QUERY_REMOVE_CURB_ZONE, {
    ...refetchTotalSpacesQueries,
    onError: resetState,
  });
  const [bulkDeleteCurbZones] = useTrackedMutation(QUERY_REMOVE_CURB_ZONES, {
    ...refetchTotalSpacesQueries,
    onError: resetState,
  });

  const [updateMultiCurbZones] = useTrackedMutation(QUERY_BULK_UPDATE_CURB_ZONE, {
    ...refetchTotalSpacesQueries,
    onError: resetState,
  });

  return {
    nearBy,
    setBounding,
    curbZones,
    loading,
    createCurbZone,
    updateCurbZone,
    removeCurbZone,
    updateMultiCurbZones,
    isResetData,
    setIsResetData,
    bulkDeleteCurbZones,
    filter,
    changeFilter,
    selectedDate,
    setSelectedDate,
    totalCalculatedSpaces: totalCalculatedSpacesResponse?.getTotalCalculatedSpaces,
  };
};
